import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Fingerprint, Copy, RefreshCw, QrCode, Power } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDateTime } from "../lib/date";

const copyToClipboard = async (text) => {
  // 1) Modern clipboard API (returns promise; may reject if blocked by permissions policy)
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to execCommand fallback
  }
  // 2) Legacy fallback — works inside iframes where the Clipboard API is blocked
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const emptyForm = {
  device_name: "",
  device_type: "fingerprint",
  serial_number: "",
  branch_id: "",
  location_description: "",
  timezone_offset_minutes: -360,
};

const TIMEZONE_OPTIONS = [
  { value: -360, label: "Costa Rica (GMT-6)" },
  { value: -300, label: "Colombia / Panamá / México DF (GMT-5)" },
  { value: -240, label: "Venezuela / Bolivia (GMT-4)" },
  { value: 0, label: "UTC (GMT+0)" },
  { value: -420, label: "México MTN / Pacific (GMT-7)" },
];

const deviceTypeLabels = {
  fingerprint: "Huella",
  face: "Rostro",
  both: "Huella + Rostro",
};

const syncStatusMap = {
  pending: { label: "Pendiente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  synced: { label: "Activo", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  error: { label: "Error", cls: "bg-red-50 text-red-700 border-red-200" },
};

const activationStatusMap = {
  pending: { label: "Sin activar", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  activated: { label: "Activado", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function Devices({ embedded = false }) {
  const [devices, setDevices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterBranch, setFilterBranch] = useState("all");
  const [regenTarget, setRegenTarget] = useState(null);   // device pending regeneration
  const [regenLoading, setRegenLoading] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeLoading, setRevokeLoading] = useState(false);

  // Render status (online/offline/idle) based on last_seen_at
  const presenceFor = (d) => {
    if (!d.last_seen_at || d.activation_status !== "activated") {
      return { label: "Sin conexión", dot: "bg-zinc-300", agoSec: null };
    }
    const seen = new Date(d.last_seen_at).getTime();
    const ago = (Date.now() - seen) / 1000;
    if (ago < 120) return { label: "En línea", dot: "bg-emerald-500", agoSec: ago };
    if (ago < 600) return { label: "Inactivo", dot: "bg-amber-500", agoSec: ago };
    return { label: "Sin conexión", dot: "bg-red-500", agoSec: ago };
  };

  const formatAgo = (sec) => {
    if (sec == null) return "nunca";
    if (sec < 60) return "hace segundos";
    if (sec < 3600) return `hace ${Math.round(sec / 60)} min`;
    if (sec < 86400) return `hace ${Math.round(sec / 3600)} h`;
    return `hace ${Math.round(sec / 86400)} d`;
  };

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterBranch !== "all") params.branch_id = filterBranch;
      const [devRes, brRes] = await Promise.all([
        api.get("/devices", { params }),
        api.get("/branches"),
      ]);
      setDevices(devRes.data);
      setBranches(brRes.data);
    } catch {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [filterBranch]);

  useEffect(() => {
    fetchData();
    // Refresh presence indicators every 30s
    const tid = setInterval(fetchData, 30_000);
    return () => clearInterval(tid);
  }, [fetchData]);

  const getBranchName = (id) => branches.find((b) => b.id === id)?.name || "\u2014";

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (d) => {
    setEditId(d.id);
    setForm({
      device_name: d.device_name,
      device_type: d.device_type,
      serial_number: d.serial_number,
      branch_id: d.branch_id,
      location_description: d.location_description || "",
      timezone_offset_minutes: d.timezone_offset_minutes ?? -360,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.device_name || !form.serial_number || !form.branch_id) {
      toast.error("Nombre, serial y sucursal son requeridos");
      return;
    }
    try {
      if (editId) {
        await api.put(`/devices/${editId}`, form);
        toast.success("Dispositivo actualizado");
      } else {
        await api.post("/devices", form);
        toast.success("Dispositivo registrado");
      }
      setDialogOpen(false);
      fetchData();
    } catch {
      toast.error("Error al guardar dispositivo");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este dispositivo?")) return;
    try {
      await api.delete(`/devices/${id}`);
      toast.success("Dispositivo eliminado");
      fetchData();
    } catch {
      toast.error("Error al eliminar dispositivo");
    }
  };

  return (
    <div data-testid="devices-page">
      <div className="flex items-center justify-between mb-6">
        {!embedded && (
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
              Dispositivos Biometricos
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Control de dispositivos de asistencia por sucursal
            </p>
          </div>
        )}
        {embedded && <div />}
        <Button
          onClick={openCreate}
          className="bg-zinc-900 text-white hover:bg-zinc-800"
          data-testid="add-device-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuevo dispositivo
        </Button>
      </div>

      <div className="mb-6">
        <Select value={filterBranch} onValueChange={setFilterBranch}>
          <SelectTrigger className="w-[200px]" data-testid="device-filter-branch">
            <SelectValue placeholder="Filtrar por sucursal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las sucursales</SelectItem>
            {branches.map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Nombre
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Sucursal
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Codigo activacion
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Estado
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden md:table-cell">
                  Tipo
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden lg:table-cell">
                  Dispositivo vinculado
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {devices.map((d) => (
                <TableRow
                  key={d.id}
                  className="border-zinc-100"
                  data-testid={`device-row-${d.id}`}
                >
                  <TableCell className="text-sm text-zinc-900 font-medium">
                    {d.device_name}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500">
                    {getBranchName(d.branch_id)}
                  </TableCell>
                  <TableCell>
                    {d.activation_code ? (
                      <div className="flex items-center gap-1.5">
                        <code className="text-sm font-mono font-semibold text-zinc-800 bg-zinc-100 px-2 py-0.5 rounded">
                          {d.activation_code}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const ok = await copyToClipboard(d.activation_code);
                            if (ok) {
                              toast.success("Codigo copiado");
                            } else {
                              toast.error(`Copia manual: ${d.activation_code}`, { duration: 6000 });
                            }
                          }}
                          data-testid={`copy-code-${d.id}`}
                        >
                          <Copy className="w-3 h-3 text-zinc-400" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRegenTarget(d);
                          }}
                          title="Regenerar codigo"
                          data-testid={`regenerate-code-${d.id}`}
                        >
                          <RefreshCw className="w-3 h-3 text-zinc-400" />
                        </Button>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={(e) => e.stopPropagation()}
                              title="Mostrar QR de activacion"
                              data-testid={`qr-code-${d.id}`}
                            >
                              <QrCode className="w-3 h-3 text-zinc-400" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-4" align="start">
                            <div className="flex flex-col items-center gap-2" data-testid={`qr-popover-${d.id}`}>
                              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-900">
                                Escanea desde el kiosko
                              </p>
                              <div className="bg-white border border-zinc-200 rounded-md p-2">
                                <QRCodeSVG
                                  value={`${window.location.origin}/kiosko?code=${encodeURIComponent(d.activation_code)}`}
                                  size={180}
                                  level="M"
                                  includeMargin={false}
                                />
                              </div>
                              <code className="text-xs font-mono text-zinc-700">{d.activation_code}</code>
                              <p className="text-[10px] text-zinc-400 text-center max-w-[180px]">
                                Abre {window.location.host}/kiosko con el codigo pre-llenado.
                              </p>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-400">Sin codigo</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge
                        variant="outline"
                        className={`font-medium w-fit ${activationStatusMap[d.activation_status]?.cls || "bg-zinc-100 text-zinc-500"}`}
                      >
                        {activationStatusMap[d.activation_status]?.label || "Sin activar"}
                      </Badge>
                      {(() => {
                        const p = presenceFor(d);
                        return (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div
                                  className="flex items-center gap-1.5 text-[11px] text-zinc-500 cursor-help w-fit"
                                  data-testid={`presence-${d.id}`}
                                >
                                  <span className={`w-1.5 h-1.5 rounded-full ${p.dot} ${p.label === "En línea" ? "animate-pulse" : ""}`} />
                                  <span>{p.label}</span>
                                  {d.pending_marks > 0 && (
                                    <span className="text-amber-600">· {d.pending_marks} pend.</span>
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="bg-zinc-900 text-white text-xs">
                                <div className="space-y-0.5">
                                  <div>Última conexión: <strong>{formatAgo(p.agoSec)}</strong></div>
                                  <div>Marcas pendientes: <strong>{d.pending_marks || 0}</strong></div>
                                  {d.last_seen_at && (
                                    <div className="text-zinc-400 text-[10px] pt-0.5">
                                      {formatDateTime(d.last_seen_at)}
                                    </div>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })()}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden md:table-cell">
                    {deviceTypeLabels[d.device_type] || d.device_type}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden lg:table-cell">
                    {d.linked_device_info ? (
                      <div>
                        <p className="text-xs text-zinc-700 font-medium">{d.linked_device_info.device_label || "Dispositivo"}</p>
                        <p className="text-[11px] text-zinc-400 truncate max-w-[200px]">{d.linked_device_info.platform || ""}</p>
                      </div>
                    ) : (
                      <span className="text-zinc-400">{"\u2014"}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {d.activation_status === "activated" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setRevokeTarget(d)}
                        title="Forzar cierre de sesión remoto"
                        data-testid={`revoke-session-${d.id}`}
                      >
                        <Power className="w-4 h-4 text-amber-500" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(d)}
                      data-testid={`edit-device-${d.id}`}
                    >
                      <Pencil className="w-4 h-4 text-zinc-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(d.id)}
                      data-testid={`delete-device-${d.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {devices.length === 0 && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-16 text-zinc-400"
                  >
                    <Fingerprint className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No hay dispositivos registrados</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              {editId ? "Editar dispositivo" : "Nuevo dispositivo"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Nombre *
              </Label>
              <Input
                value={form.device_name}
                onChange={(e) =>
                  setForm({ ...form, device_name: e.target.value })
                }
                placeholder="Lector entrada principal"
                data-testid="device-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Tipo *
                </Label>
                <Select
                  value={form.device_type}
                  onValueChange={(v) => setForm({ ...form, device_type: v })}
                >
                  <SelectTrigger data-testid="device-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fingerprint">Huella</SelectItem>
                    <SelectItem value="face">Rostro</SelectItem>
                    <SelectItem value="both">Huella + Rostro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Serial *
                </Label>
                <Input
                  value={form.serial_number}
                  onChange={(e) =>
                    setForm({ ...form, serial_number: e.target.value })
                  }
                  placeholder="SN-12345"
                  data-testid="device-serial-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Sucursal *
              </Label>
              <Select
                value={form.branch_id}
                onValueChange={(v) => setForm({ ...form, branch_id: v })}
              >
                <SelectTrigger data-testid="device-branch-select">
                  <SelectValue placeholder="Seleccionar sucursal" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Ubicacion
              </Label>
              <Input
                value={form.location_description}
                onChange={(e) =>
                  setForm({ ...form, location_description: e.target.value })
                }
                placeholder="Entrada principal"
                data-testid="device-location-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Zona horaria del dispositivo
              </Label>
              <Select
                value={String(form.timezone_offset_minutes ?? -360)}
                onValueChange={(v) =>
                  setForm({ ...form, timezone_offset_minutes: Number(v) })
                }
              >
                <SelectTrigger data-testid="device-timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <SelectItem key={tz.value} value={String(tz.value)}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-zinc-500 mt-1">
                La hora de cada marca se guardará en esta zona horaria.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="device-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="device-submit-button"
            >
              {editId ? "Actualizar" : "Registrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate-code confirmation (robust for tablets) */}
      <AlertDialog open={!!regenTarget} onOpenChange={(o) => !o && !regenLoading && setRegenTarget(null)}>
        <AlertDialogContent data-testid="regenerate-code-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerar codigo de activacion</AlertDialogTitle>
            <AlertDialogDescription>
              {regenTarget?.activation_status === "activated" ? (
                <>
                  El dispositivo <strong>{regenTarget?.device_name}</strong> esta vinculado y sera
                  desvinculado inmediatamente. Tendras que activarlo de nuevo usando el nuevo codigo.
                </>
              ) : (
                <>
                  Se generara un nuevo codigo de activacion para <strong>{regenTarget?.device_name}</strong>.
                  El codigo anterior dejara de ser valido.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={regenLoading} data-testid="regenerate-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={regenLoading}
              onClick={async () => {
                if (!regenTarget) return;
                setRegenLoading(true);
                try {
                  const { data } = await api.post(`/devices/${regenTarget.id}/regenerate-code`);
                  toast.success(`Nuevo codigo: ${data.activation_code}`);
                  setRegenTarget(null);
                  await fetchData();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || err?.message || "Error al regenerar codigo");
                } finally {
                  setRegenLoading(false);
                }
              }}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="regenerate-confirm"
            >
              {regenLoading ? "Regenerando..." : "Regenerar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke session confirmation */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && !revokeLoading && setRevokeTarget(null)}>
        <AlertDialogContent data-testid="revoke-session-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Forzar cierre de sesión remoto</AlertDialogTitle>
            <AlertDialogDescription>
              El kiosko <strong>{revokeTarget?.device_name}</strong> se desconectará en su próximo latido
              (≤ 60s) y volverá a pedir el código de activación. Las marcas pendientes en cola se sincronizan
              primero. <br /><br />
              Use esta opción si el dispositivo fue robado, perdido o cambiado de sucursal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeLoading} data-testid="revoke-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={revokeLoading}
              onClick={async () => {
                if (!revokeTarget) return;
                setRevokeLoading(true);
                try {
                  await api.post(`/devices/${revokeTarget.id}/revoke-session`);
                  toast.success("Sesión revocada. El kiosko se desconectará en breve.");
                  setRevokeTarget(null);
                  await fetchData();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Error al revocar sesión");
                } finally {
                  setRevokeLoading(false);
                }
              }}
              className="bg-amber-600 text-white hover:bg-amber-700"
              data-testid="revoke-confirm"
            >
              {revokeLoading ? "Revocando..." : "Cerrar sesión"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
